import type { Resolution } from './types';

export type RatioGuardProfileId =
  'dxv3' | 'hap' | 'notchlc' | 'ndi-high-bandwidth' | 'ndi-hx' | 'prores-422' | 'h264' | 'h265';

export type RatioGuardProfileGroup = 'Live playback' | 'Transport' | 'Post-production' | 'Delivery';

export interface AspectRatio {
  width: number;
  height: number;
}

export interface RatioGuardProfile {
  id: RatioGuardProfileId;
  group: RatioGuardProfileGroup;
  label: string;
  shortLabel: string;
  requirement: string;
  description: string;
  requiredWidthMultiple?: number;
  requiredHeightMultiple?: number;
  preferredWidthMultiple?: number;
  preferredHeightMultiple?: number;
  preferredForCustomRasterOnly?: boolean;
  maximumDimension?: number;
  usesGpuTexture?: boolean;
  standardRasterAdvisory?: boolean;
}

export interface RatioGuardIssue {
  kind: 'required' | 'recommended' | 'limit' | 'advisory';
  message: string;
}

export interface ResolutionCandidate {
  resolution: Resolution;
  deltaWidth: number;
  deltaHeight: number;
  aspectDeviationPercent: number;
}

export interface RatioGuardProfileResult {
  profile: RatioGuardProfile;
  status: 'pass' | 'warning' | 'adjust';
  issues: RatioGuardIssue[];
  closest: ResolutionCandidate | null;
}

export interface RatioGuardAnalysis {
  target: Resolution;
  aspect: AspectRatio;
  profileResults: RatioGuardProfileResult[];
  requiredWidthMultiple: number;
  requiredHeightMultiple: number;
  recommendedWidthMultiple: number;
  recommendedHeightMultiple: number;
  maximumWidth: number | null;
  maximumHeight: number | null;
  recommended: ResolutionCandidate | null;
  pad: ResolutionCandidate | null;
  crop: ResolutionCandidate | null;
  exactAspect: ResolutionCandidate | null;
}

const STANDARD_RASTERS: Resolution[] = [
  { width: 720, height: 480 },
  { width: 720, height: 576 },
  { width: 1280, height: 720 },
  { width: 1920, height: 1080 },
  { width: 2048, height: 1080 },
  { width: 3840, height: 2160 },
  { width: 4096, height: 2160 },
  { width: 7680, height: 4320 },
  { width: 8192, height: 4320 },
];

export const RATIO_GUARD_PROFILES: readonly RatioGuardProfile[] = [
  {
    id: 'dxv3',
    group: 'Live playback',
    label: 'Resolume DXV 3',
    shortLabel: 'DXV 3',
    requirement: 'Codec-friendly 16 × 16 storage alignment and GPU texture limit.',
    description:
      'DXV encoders can pad internally. Custom rasters aligned to 16 pixels avoid ambiguous edge handling in playback pipelines.',
    preferredWidthMultiple: 16,
    preferredHeightMultiple: 16,
    preferredForCustomRasterOnly: true,
    usesGpuTexture: true,
  },
  {
    id: 'hap',
    group: 'Live playback',
    label: 'HAP family',
    shortLabel: 'HAP',
    requirement: 'Width and height must be divisible by 4; GPU texture limit applies.',
    description: 'Covers Hap, Hap Alpha, Hap Q, Hap Q Alpha, Hap R and Hap HDR geometry.',
    requiredWidthMultiple: 4,
    requiredHeightMultiple: 4,
    usesGpuTexture: true,
  },
  {
    id: 'notchlc',
    group: 'Live playback',
    label: 'NotchLC',
    shortLabel: 'NotchLC',
    requirement: 'Maximum 16,384 × 16,384 and the current GPU texture limit.',
    description: 'NotchLC has no documented pixel-divisibility requirement.',
    maximumDimension: 16_384,
    usesGpuTexture: true,
  },
  {
    id: 'ndi-high-bandwidth',
    group: 'Transport',
    label: 'NDI High Bandwidth',
    shortLabel: 'NDI HB',
    requirement: 'Width must be divisible by 2 for the internal 4:2:2 representation.',
    description: 'Custom aspect ratios are allowed, but receiver capabilities still vary.',
    requiredWidthMultiple: 2,
  },
  {
    id: 'ndi-hx',
    group: 'Transport',
    label: 'NDI HX2 / HX3',
    shortLabel: 'NDI HX',
    requirement: 'Width and height must be even for the typical 4:2:0 pipeline.',
    description: 'Custom rasters may work but are outside the commonly certified raster set.',
    requiredWidthMultiple: 2,
    requiredHeightMultiple: 2,
    standardRasterAdvisory: true,
  },
  {
    id: 'prores-422',
    group: 'Post-production',
    label: 'Apple ProRes 422',
    shortLabel: 'ProRes 422',
    requirement: 'Even width is the safest geometry for a 4:2:2 pipeline.',
    description: 'This is a conservative interoperability check for encoders and host software.',
    requiredWidthMultiple: 2,
  },
  {
    id: 'h264',
    group: 'Delivery',
    label: 'H.264 / AVC 4:2:0',
    shortLabel: 'H.264',
    requirement: 'Width and height must be even; 16-pixel alignment is advised for custom rasters.',
    description: 'Standard rasters such as 1920 × 1080 are not flagged for macroblock padding.',
    requiredWidthMultiple: 2,
    requiredHeightMultiple: 2,
    preferredWidthMultiple: 16,
    preferredHeightMultiple: 16,
    preferredForCustomRasterOnly: true,
  },
  {
    id: 'h265',
    group: 'Delivery',
    label: 'H.265 / HEVC 4:2:0',
    shortLabel: 'H.265',
    requirement: 'Width and height must be even; 8-pixel alignment is advised for custom rasters.',
    description: 'Hardware encoder and decoder limits remain device-specific.',
    requiredWidthMultiple: 2,
    requiredHeightMultiple: 2,
    preferredWidthMultiple: 8,
    preferredHeightMultiple: 8,
    preferredForCustomRasterOnly: true,
  },
] as const;

export const DEFAULT_RATIO_GUARD_PROFILES: readonly RatioGuardProfileId[] = [
  'dxv3',
  'hap',
  'notchlc',
  'ndi-high-bandwidth',
  'prores-422',
];

export function greatestCommonDivisor(a: number, b: number): number {
  let left = Math.abs(Math.trunc(a));
  let right = Math.abs(Math.trunc(b));
  while (right !== 0) {
    const remainder = left % right;
    left = right;
    right = remainder;
  }
  return left || 1;
}

export function leastCommonMultiple(a: number, b: number): number {
  const left = Math.max(1, Math.trunc(a));
  const right = Math.max(1, Math.trunc(b));
  return Math.abs((left / greatestCommonDivisor(left, right)) * right);
}

export function simplifyAspectRatio(resolution: Resolution): AspectRatio {
  const divisor = greatestCommonDivisor(resolution.width, resolution.height);
  return {
    width: resolution.width / divisor,
    height: resolution.height / divisor,
  };
}

export function calculateLockedResolution(
  leading: 'width' | 'height',
  value: number,
  aspect: AspectRatio,
): Resolution {
  if (leading === 'width') {
    return {
      width: Math.round(value),
      height: Math.max(1, Math.round((value * aspect.height) / aspect.width)),
    };
  }
  return {
    width: Math.max(1, Math.round((value * aspect.width) / aspect.height)),
    height: Math.round(value),
  };
}

export function isStandardRaster(resolution: Resolution): boolean {
  return STANDARD_RASTERS.some(
    (item) =>
      (item.width === resolution.width && item.height === resolution.height) ||
      (item.width === resolution.height && item.height === resolution.width),
  );
}

export function analyzeRatioGuard(
  target: Resolution,
  profileIds: readonly RatioGuardProfileId[],
  aspect: AspectRatio = simplifyAspectRatio(target),
  gpuTextureLimit: number | null = null,
): RatioGuardAnalysis {
  const selectedProfiles = profileIds
    .map((id) => RATIO_GUARD_PROFILES.find((profile) => profile.id === id))
    .filter((profile): profile is RatioGuardProfile => Boolean(profile));
  const constraints = selectedProfiles.map((profile) =>
    createConstraints(profile, target, gpuTextureLimit),
  );
  const requiredWidthMultiple = constraints.reduce(
    (value, constraint) => leastCommonMultiple(value, constraint.requiredWidthMultiple),
    1,
  );
  const requiredHeightMultiple = constraints.reduce(
    (value, constraint) => leastCommonMultiple(value, constraint.requiredHeightMultiple),
    1,
  );
  const recommendedWidthMultiple = constraints.reduce(
    (value, constraint) => leastCommonMultiple(value, constraint.recommendedWidthMultiple),
    requiredWidthMultiple,
  );
  const recommendedHeightMultiple = constraints.reduce(
    (value, constraint) => leastCommonMultiple(value, constraint.recommendedHeightMultiple),
    requiredHeightMultiple,
  );
  const maximumWidth = minimumLimit(constraints.map((constraint) => constraint.maximumWidth));
  const maximumHeight = minimumLimit(constraints.map((constraint) => constraint.maximumHeight));
  const recommendationConstraints = {
    widthMultiple: recommendedWidthMultiple,
    heightMultiple: recommendedHeightMultiple,
    maximumWidth,
    maximumHeight,
  };
  const pad = buildDirectionalCandidate(target, aspect, recommendationConstraints, 'up');
  const crop = buildDirectionalCandidate(target, aspect, recommendationConstraints, 'down');
  const recommended = chooseRecommendedCandidate(target, pad, crop, maximumWidth, maximumHeight);
  const exactAspect = buildExactAspectCandidate(target, aspect, recommendationConstraints);

  return {
    target,
    aspect,
    profileResults: selectedProfiles.map((profile, index) =>
      evaluateProfile(profile, target, aspect, constraints[index]!),
    ),
    requiredWidthMultiple,
    requiredHeightMultiple,
    recommendedWidthMultiple,
    recommendedHeightMultiple,
    maximumWidth,
    maximumHeight,
    recommended,
    pad,
    crop,
    exactAspect,
  };
}

interface EffectiveConstraints {
  requiredWidthMultiple: number;
  requiredHeightMultiple: number;
  recommendedWidthMultiple: number;
  recommendedHeightMultiple: number;
  maximumWidth: number | null;
  maximumHeight: number | null;
  preferredActive: boolean;
}

interface CandidateConstraints {
  widthMultiple: number;
  heightMultiple: number;
  maximumWidth: number | null;
  maximumHeight: number | null;
}

function createConstraints(
  profile: RatioGuardProfile,
  target: Resolution,
  gpuTextureLimit: number | null,
): EffectiveConstraints {
  const preferredActive =
    Boolean(profile.preferredWidthMultiple || profile.preferredHeightMultiple) &&
    (!profile.preferredForCustomRasterOnly || !isStandardRaster(target));
  const staticMaximum = profile.maximumDimension ?? null;
  const textureMaximum = profile.usesGpuTexture ? gpuTextureLimit : null;
  const maximum = minimumLimit([staticMaximum, textureMaximum]);
  const requiredWidthMultiple = profile.requiredWidthMultiple ?? 1;
  const requiredHeightMultiple = profile.requiredHeightMultiple ?? 1;
  return {
    requiredWidthMultiple,
    requiredHeightMultiple,
    recommendedWidthMultiple: leastCommonMultiple(
      requiredWidthMultiple,
      preferredActive ? (profile.preferredWidthMultiple ?? 1) : 1,
    ),
    recommendedHeightMultiple: leastCommonMultiple(
      requiredHeightMultiple,
      preferredActive ? (profile.preferredHeightMultiple ?? 1) : 1,
    ),
    maximumWidth: maximum,
    maximumHeight: maximum,
    preferredActive,
  };
}

function evaluateProfile(
  profile: RatioGuardProfile,
  target: Resolution,
  aspect: AspectRatio,
  constraints: EffectiveConstraints,
): RatioGuardProfileResult {
  const issues: RatioGuardIssue[] = [];
  if (
    target.width % constraints.requiredWidthMultiple !== 0 ||
    target.height % constraints.requiredHeightMultiple !== 0
  ) {
    issues.push({ kind: 'required', message: multipleIssueMessage(constraints, false) });
  }
  if (
    constraints.maximumWidth !== null &&
    (target.width > constraints.maximumWidth || target.height > constraints.maximumHeight!)
  ) {
    issues.push({
      kind: 'limit',
      message: `Exceeds the available ${formatInteger(constraints.maximumWidth)} px texture limit.`,
    });
  }
  const missesPreferred =
    constraints.preferredActive &&
    (target.width % constraints.recommendedWidthMultiple !== 0 ||
      target.height % constraints.recommendedHeightMultiple !== 0);
  if (missesPreferred) {
    issues.push({ kind: 'recommended', message: multipleIssueMessage(constraints, true) });
  }
  if (profile.standardRasterAdvisory && !isStandardRaster(target)) {
    issues.push({
      kind: 'advisory',
      message: 'Custom raster: verify it on the exact NDI HX sender and receiver.',
    });
  }

  const hasRequiredIssue = issues.some(
    (issue) => issue.kind === 'required' || issue.kind === 'limit',
  );
  const profileConstraints: CandidateConstraints = {
    widthMultiple: constraints.recommendedWidthMultiple,
    heightMultiple: constraints.recommendedHeightMultiple,
    maximumWidth: constraints.maximumWidth,
    maximumHeight: constraints.maximumHeight,
  };
  const pad = buildDirectionalCandidate(target, aspect, profileConstraints, 'up');
  const crop = buildDirectionalCandidate(target, aspect, profileConstraints, 'down');

  return {
    profile,
    status: hasRequiredIssue ? 'adjust' : issues.length ? 'warning' : 'pass',
    issues,
    closest: issues.length
      ? chooseRecommendedCandidate(
          target,
          pad,
          crop,
          constraints.maximumWidth,
          constraints.maximumHeight,
        )
      : createCandidate(target, target, aspect),
  };
}

function multipleIssueMessage(constraints: EffectiveConstraints, preferred: boolean): string {
  const width = preferred
    ? constraints.recommendedWidthMultiple
    : constraints.requiredWidthMultiple;
  const height = preferred
    ? constraints.recommendedHeightMultiple
    : constraints.requiredHeightMultiple;
  const prefix = preferred ? 'For a custom raster, prefer' : 'Requires';
  if (width === height) return `${prefix} width and height divisible by ${width}.`;
  if (width > 1 && height === 1) return `${prefix} width divisible by ${width}.`;
  if (height > 1 && width === 1) return `${prefix} height divisible by ${height}.`;
  return `${prefix} width divisible by ${width} and height by ${height}.`;
}

function buildDirectionalCandidate(
  target: Resolution,
  aspect: AspectRatio,
  constraints: CandidateConstraints,
  direction: 'up' | 'down',
): ResolutionCandidate | null {
  const align = direction === 'up' ? alignUp : alignDown;
  let width = align(target.width, constraints.widthMultiple);
  let height = align(target.height, constraints.heightMultiple);
  if (width < 1 || height < 1) return null;

  if (
    constraints.maximumWidth !== null &&
    (width > constraints.maximumWidth || height > constraints.maximumHeight!)
  ) {
    if (direction === 'up') return null;
    width = alignDown(Math.min(width, constraints.maximumWidth), constraints.widthMultiple);
    height = alignDown(Math.min(height, constraints.maximumHeight!), constraints.heightMultiple);
  }
  if (width < 1 || height < 1) return null;
  return createCandidate(target, { width, height }, aspect);
}

function buildExactAspectCandidate(
  target: Resolution,
  aspect: AspectRatio,
  constraints: CandidateConstraints,
): ResolutionCandidate | null {
  const widthStep =
    constraints.widthMultiple / greatestCommonDivisor(aspect.width, constraints.widthMultiple);
  const heightStep =
    constraints.heightMultiple / greatestCommonDivisor(aspect.height, constraints.heightMultiple);
  const scaleStep = leastCommonMultiple(widthStep, heightStep);
  const targetScale = (target.width / aspect.width + target.height / aspect.height) / 2;
  const lowerScale = Math.max(scaleStep, Math.floor(targetScale / scaleStep) * scaleStep);
  const upperScale = Math.max(scaleStep, Math.ceil(targetScale / scaleStep) * scaleStep);
  const candidates = [...new Set([lowerScale, upperScale])]
    .map((scale) => ({ width: aspect.width * scale, height: aspect.height * scale }))
    .filter(
      (resolution) =>
        (!constraints.maximumWidth || resolution.width <= constraints.maximumWidth) &&
        (!constraints.maximumHeight || resolution.height <= constraints.maximumHeight),
    )
    .map((resolution) => createCandidate(target, resolution, aspect));
  return (
    candidates.sort((left, right) => candidateDistance(left) - candidateDistance(right))[0] ?? null
  );
}

function chooseRecommendedCandidate(
  target: Resolution,
  pad: ResolutionCandidate | null,
  crop: ResolutionCandidate | null,
  maximumWidth: number | null,
  maximumHeight: number | null,
): ResolutionCandidate | null {
  const targetExceedsLimit =
    maximumWidth !== null && (target.width > maximumWidth || target.height > maximumHeight!);
  if (targetExceedsLimit) return crop;
  return pad ?? crop;
}

function createCandidate(
  target: Resolution,
  resolution: Resolution,
  aspect: AspectRatio,
): ResolutionCandidate {
  const sourceRatio = aspect.width / aspect.height;
  const resultRatio = resolution.width / resolution.height;
  return {
    resolution,
    deltaWidth: resolution.width - target.width,
    deltaHeight: resolution.height - target.height,
    aspectDeviationPercent: Math.abs((resultRatio / sourceRatio - 1) * 100),
  };
}

function candidateDistance(candidate: ResolutionCandidate): number {
  return Math.abs(candidate.deltaWidth) + Math.abs(candidate.deltaHeight);
}

function alignUp(value: number, multiple: number): number {
  return Math.ceil(value / multiple) * multiple;
}

function alignDown(value: number, multiple: number): number {
  return Math.floor(value / multiple) * multiple;
}

function minimumLimit(values: Array<number | null>): number | null {
  const defined = values.filter((value): value is number => value !== null && value > 0);
  return defined.length ? Math.min(...defined) : null;
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}
